import { useEffect, useRef } from "react";
import { Ban, Check, UploadCloud } from "lucide-react";
import { cn } from "@/lib/utils";
import { DOC_ACCEPT, MAX_DOC_FILE_MB, withDocMimeType } from "@/utils/docFile";
import { BusyIndicator } from "@/components/BusyIndicator";
import { FILE_TYPE_ERROR, ONE_FILE_ERROR, fileDropTone, useFileDrop } from "@/hooks/useFileDrop";
import { useT } from "@/features/onboarding/i18n";
import { useFilePreview } from "./useFilePreview";

export function FileDrop({
  prompt, busy, file, error, previousNote, onFile, onRemove,
}: {
  prompt: string;
  busy?: boolean;
  file: File | null;
  error?: string | null;
  previousNote?: string | null;
  onFile: (f: File) => void;
  onRemove?: () => void;
}) {
  const t = useT();
  const drop = useFileDrop({ accept: DOC_ACCEPT, busy, onFile: (f) => onFile(withDocMimeType(f)) });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const preview = useFilePreview(file);
  const hasFile = !!file;
  const rejection = drop.rejection === FILE_TYPE_ERROR
    ? t("file.typeError")
    : drop.rejection === ONE_FILE_ERROR
      ? t("file.oneFile")
      : drop.rejection;
  const message = rejection ?? error ?? null;
  const reattach = !hasFile && !message && !!previousNote;

  useEffect(() => {
    if (message) buttonRef.current?.focus();
  }, [message]);

  function remove() {
    drop.clearRejection();
    onRemove?.();
  }

  return (
    <div>
      <button
        ref={buttonRef}
        type="button"
        aria-disabled={busy}
        aria-busy={busy}
        onClick={drop.openPicker}
        {...drop.zoneProps}
        className={cn(
          "flex w-full flex-col items-center justify-center gap-1 rounded-md border-2 px-4 py-6 text-center text-sm transition-colors [&_*]:pointer-events-none",
          fileDropTone({ phase: drop.phase, busy, invalid: !!message, filled: hasFile, attention: reattach }),
        )}
      >
        {busy ? (
          <BusyIndicator label={t("file.scanning")} />
        ) : drop.phase === "blocked" ? (
          <>
            <Ban className="size-5" aria-hidden="true" />
            <span className="font-medium">{t("file.unsupported")}</span>
          </>
        ) : hasFile && drop.phase === "idle" ? (
          <>
            <Check className="size-5" strokeWidth={2.5} aria-hidden="true" />
            <span className="font-medium">
              <span className="sm:hidden">{t("file.uploadedTap")}</span>
              <span className="hidden sm:inline">{t("file.uploadedDrop")}</span>
            </span>
          </>
        ) : (
          <>
            <UploadCloud className="size-5" aria-hidden="true" />
            <span className={cn("font-medium", !message && !reattach && "text-foreground")}>
              <span className="sm:hidden">{prompt}</span>
              <span className="hidden sm:inline">
                {t("file.dragDrop")} <span className="text-primary">{t("file.browse")}</span>
              </span>
            </span>
            <span className="text-xs text-muted-foreground">{t("file.limits", { mb: MAX_DOC_FILE_MB })}</span>
          </>
        )}
      </button>
      {message && !busy && (
        <p className="mt-1.5 text-xs font-medium text-destructive" role="alert">{message}</p>
      )}
      {reattach && !busy && (
        <p className="mt-1.5 text-xs text-amber-700">{previousNote}</p>
      )}
      {file && !busy && (
        <div className="mt-1.5 flex items-center gap-3">
          {preview && (
            <img
              src={preview}
              alt={t("file.previewAlt", { name: file.name })}
              className="size-14 shrink-0 rounded-md border border-emerald-200 object-cover"
            />
          )}
          <p className="min-w-0 flex-1 truncate text-xs text-emerald-700">{t("file.attached", { name: file.name })}</p>
          {onRemove && (
            <button
              type="button"
              onClick={remove}
              className="shrink-0 text-xs font-medium text-muted-foreground underline underline-offset-2 hover:text-foreground"
            >
              {t("file.remove")}
            </button>
          )}
        </div>
      )}
      <input {...drop.inputProps} />
    </div>
  );
}
